import { Navigate, NavLink, useNavigate, useSearchParams } from 'react-router'
import { useSignInWithLink } from '../../app/hooks/UseSignInWithLink.ts'
import { useEffect, useRef, useState } from 'react'
import { useAppAuth } from '../../app/hooks/UseAppAuth.ts'
import GenericErrorPage from '../../pages/GenericErrorPage.tsx'
import SignInLoadingSpinner from './SignInLoadingSpinner.tsx'

export default function SignInWithLink() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')

  const effectRan = useRef(false) // Track execution
  const [hasEmailError, setHasEmailError] = useState<boolean>(false)
  const [loginState, signInWithLink] = useSignInWithLink()
  const { isAuthenticated, user, userLoaded } = useAppAuth()

  useEffect(() => {
    if (!code || effectRan.current) return
    effectRan.current = true // Mark as executed
    fetchEmailByCode(code)
    // fetchEmailByCode is a stable run-once handler guarded by effectRan; intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code])

  useEffect(() => {
    if (user && userLoaded && isAuthenticated) {
      const currentClubId = user.clubs[0].id.toString()
      navigate(`/club/${currentClubId}`)
    }
  }, [user, userLoaded, isAuthenticated, navigate])

  // NOTE: RTK Query (lazy query) was initially attempted here but consistently faced initialization issues.
  // Due to persistent state and hook update problems, it was refactored to use a simple fetch call, which resolved all issues.
  const fetchEmailByCode = (code: string) => {
    fetch(`${import.meta.env.VITE_WEB_API_BASE_URL}/auth/sign-in-email/${code}`)
      .then((response) => {
        if (!response.ok) {
          setHasEmailError(true)
          return null
        }
        return response.json()
      })
      .then((data) => {
        if (data && data.email && data.email !== '') {
          signInWithLink(data.email)
        } else {
          setHasEmailError(true)
        }
      })
      .catch(() => {
        setHasEmailError(true)
      })
  }

  if (loginState === 'FAILED' || hasEmailError) {
    return (
      <GenericErrorPage
        title="Sign-In Failed"
        description="An error occurred during sign-in, or the link you used is invalid, expired, or already used. Each sign-in link can only be used once. Please request a new link or try signing in in another way">
        <NavLink
          to="/login"
          className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Back to Login
        </NavLink>
      </GenericErrorPage>
    )
  }

  if (loginState !== 'COMPLETED' || !user || !userLoaded || !isAuthenticated) {
    return <SignInLoadingSpinner />
  }

  if (user.clubs.length === 0) {
    return <Navigate replace to={`/clubs`} />
  }

  return <Navigate replace to={`/club/${user.clubs[0].id.toString()}`} />
}
